import type { Market } from '../types';

type GoalKey =
  | 'defender_preco'
  | 'abrir_espaco'
  | 'ampliar_mix'
  | 'aumentar_share'
  | 'melhorar_execucao'
  | 'ganhar_exposicao'
  | 'construir_plano'
  | 'introduzir_sku'
  | 'fortalecer_parceria';

export interface ScenarioArchetype {
  id: string;
  label: string;
  market?: Market;
  regexes: RegExp[];
  goalBias?: GoalKey[];
  focus: string;
  opcAngle: string;
  spinAngle: string;
  epaAngle: string;
  sineAngle: string;
}

export const SCENARIO_ARCHETYPES: ScenarioArchetype[] = [
  {
    id: 'discount_out_of_band',
    label: 'Pedido de desconto fora da banda',
    market: 'interno',
    regexes: [/desconto/, /fora da banda|abaixo da banda|ppc/],
    goalBias: ['defender_preco'],
    focus: 'blindar preço sem perder avanço comercial',
    opcAngle: 'entrar com dados de giro, margem e PPV para mostrar que a discussão não pode ficar no desconto isolado',
    spinAngle: 'aprofundar o custo da pressão de preço na rentabilidade da categoria e na percepção de valor',
    epaAngle: 'comparar concessão imediata com resultado sustentável e exigir contrapartida tangível',
    sineAngle: 'tirar o cliente da lógica de preço e conduzir para uma troca de valor por execução, mix ou volume'
  },
  {
    id: 'rupture_execution',
    label: 'Ruptura e execução fraca no PDV',
    market: 'interno',
    regexes: [/ruptura|falta|sem estoque/, /gondola|exposicao|pdv|reposicao|execucao/],
    goalBias: ['melhorar_execucao', 'ganhar_exposicao'],
    focus: 'resolver perda de venda e proteger presença na gôndola',
    opcAngle: 'entrar com leitura de loja, evidência de ruptura e proposta rápida de correção',
    spinAngle: 'quantificar perda semanal por ruptura e impacto no giro da categoria',
    epaAngle: 'educar o cliente sobre o custo invisível da ruptura e personalizar o plano de execução',
    sineAngle: 'começar na dor operacional, aprofundar perda real e fechar com plano de reposição e exposição'
  },
  {
    id: 'premium_mix',
    label: 'Introdução de SKU premium ou de margem',
    market: 'interno',
    regexes: [/premium|valor agregado|maior margem|cash margin|novo sku|lancamento/],
    goalBias: ['introduzir_sku', 'ampliar_mix'],
    focus: 'elevar rentabilidade da categoria com diferenciação',
    opcAngle: 'mostrar que a expansão de mix precisa elevar margem, não apenas encher gôndola',
    spinAngle: 'investigar espaço, ocasião de consumo, ticket e potencial de cash margin',
    epaAngle: 'mudar o critério da decisão de volume para rentabilidade e diferenciação',
    sineAngle: 'abrir pela oportunidade, aprofundar o dinheiro deixado na mesa e fechar com teste controlado'
  },
  {
    id: 'share_competitor',
    label: 'Concorrente ganhando espaço',
    market: 'interno',
    regexes: [/concorrente|share|espaco do concorrente|perdendo espaco/],
    goalBias: ['aumentar_share', 'abrir_espaco'],
    focus: 'retomar espaço com narrativa superior e execução consistente',
    opcAngle: 'entrar sabendo onde a C.Vale está perdendo espaço e qual espaço quer recuperar',
    spinAngle: 'explorar por que o concorrente entrou e onde a categoria está ficando vulnerável',
    epaAngle: 'reorientar a conversa para confiança, execução, categoria e resultado, não para reação emocional',
    sineAngle: 'reposicionar a ameaça competitiva como oportunidade de reorganizar a categoria'
  },
  {
    id: 'export_price_pressure',
    label: 'Pressão de preço no mercado externo',
    market: 'externo',
    regexes: [/reducao|desconto|preco/, /embarque|importador|trading|fornecedor|volume/],
    goalBias: ['defender_preco', 'fortalecer_parceria'],
    focus: 'defender preço com base em continuidade, padrão e custo de troca',
    opcAngle: 'entrar com histórico de regularidade, conformidade e proposta de calendário ou volume',
    spinAngle: 'explorar risco operacional, custo de troca e impacto de instabilidade para o cliente',
    epaAngle: 'educar sobre custo total de fornecimento e assumir o controle com proposta de vigência e volume',
    sineAngle: 'sair do spot e conduzir para uma relação previsível de médio prazo'
  },
  {
    id: 'joint_business_plan',
    label: 'Construção de plano conjunto ou PDN',
    regexes: [/pdn|plano|jbp|crescimento conjunto|investimento|contrapartida/],
    goalBias: ['construir_plano', 'fortalecer_parceria'],
    focus: 'transformar demanda comercial em plano aprovado e mensurável',
    opcAngle: 'organizar claramente objetivo, investimento, contrapartida e ganho esperado',
    spinAngle: 'aprofundar o que o cliente quer destravar e qual retorno espera do investimento',
    epaAngle: 'tirar o plano do campo da promessa e levar para viabilidade, ROI e disciplina de execução',
    sineAngle: 'começar no cenário do negócio, aprofundar a oportunidade e fechar com plano e indicadores'
  }
];
